import { Injectable } from '@angular/core';
import '../types/window.types';

@Injectable({
  providedIn: 'root'
})
export class FlutterBridgeService {
  private readonly PENDING_DATA_KEY = 'flutter_pending_data';
  private readonly BRIDGE_CHECK_INTERVAL = 100; // ms
  private readonly BRIDGE_CHECK_TIMEOUT = 10000; // ms

  isBridgeAvailable(): boolean {
    return !!window.webkit?.messageHandlers?.flutter;
  }

  savePendingData(data: any): void {
    try {
      sessionStorage.setItem(this.PENDING_DATA_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving pending data:', error);
    }
  }

  getPendingData(): any | null {
    try {
      const data = sessionStorage.getItem(this.PENDING_DATA_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error getting pending data:', error);
      return null;
    }
  }

  clearPendingData(): void {
    sessionStorage.removeItem(this.PENDING_DATA_KEY);
  }

  async waitForBridge(): Promise<boolean> {
    if (this.isBridgeAvailable()) {
      return true;
    }

    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.isBridgeAvailable()) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (Date.now() - startTime > this.BRIDGE_CHECK_TIMEOUT) {
          clearInterval(checkInterval);
          console.warn('Flutter bridge wait timeout');
          resolve(false);
        }
      }, this.BRIDGE_CHECK_INTERVAL);
    });
  }

  sendDataToFlutter(obj: any): boolean {
    if (this.isBridgeAvailable()) {
      try {
        window.webkit!.messageHandlers!.flutter!.postMessage(JSON.stringify(obj));
        return true;
      } catch (error) {
        console.error('Error sending data to Flutter:', error);
        return false;
      }
    } else {
      console.warn('Flutter bridge not available');
      return false;
    }
  }

  async sendDataToFlutterWithWait(obj: any): Promise<boolean> {
    const bridgeAvailable = await this.waitForBridge();

    if (bridgeAvailable) {
      return this.sendDataToFlutter(obj);
    }

    console.error('Failed to send data to Flutter: bridge unavailable after timeout');
    return false;
  }

  async restoreAndSendPendingData(): Promise<boolean> {
    const pendingData = this.getPendingData();

    if (!pendingData) {
      console.warn('No pending data to send');
      return false;
    }

    const success = await this.sendDataToFlutterWithWait(pendingData);

    if (success) {
      this.clearPendingData();
    }

    return success;
  }
}

